import React, { useEffect, useMemo, useState } from "react";
import { Head, useForm } from "@inertiajs/inertia-react";
import Authenticated from "@/Layouts/Authenticated";
import Input from "@/Components/Input";
import Label from "@/Components/Label";
import { DateTime } from "luxon";
import Button from "@/Components/Button";
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { Inertia } from "@inertiajs/inertia";
import ExportButton from "@/Components/ExportButton";
import { formatDate } from "@/helpers/date";
import Table from "./Table";
import Spinner from "@/Components/Spinner";
import RemoveExportButton from "@/Components/Export/RemoveExportButton";

const Main = (props) => {
    const [loadingScheduling, setLoadingScheduling] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [isChecking, setIsChecking] = useState(false)


    const { data, setData } = useForm({
        start_date: "",
        start_time: "",
        end_date: "",
        end_time: "",
        type: "excel"
    });

    const [combinedDateTime, setCombinedDateTime] = useState({
        startDateTime: "",
        endDateTime: "",
    });

    const handleChange = (event) => {
        const { name, value } = event.target;
        setData(name, value);
    };

    useEffect(() => {
        const { start_date, start_time, end_date, end_time } = data;
        const startDateTime = combineDateTime(start_date, start_time);
        const endDateTime = combineDateTime(end_date, end_time);
        setCombinedDateTime({ startDateTime, endDateTime });
    }, [data]);

    useEffect(() => {
        if (!jobId) return;
        setIsChecking(true);

        const interval = setInterval(async () => {
            try {
                const response = await axios.get(route('export.request.check', { uuid: jobId }));
                if (response.data.status === 'success') {
                    const messages = {
                        'exportation.completed': 'El reporte se generó correctamente.',
                    }

                    const message = messages[response.data.code];
                    toast.success(message);
                    setJobId(null);

                    if (response.data.code === 'exportation.completed') {
                        clearInterval(interval)
                        Inertia.reload({ only: ['exportRequests'] })
                        setIsChecking(false);
                    }
                }
            } catch (error) {
                console.log({error});
                setIsChecking(false);
            }
        }, 5000);
    }, [jobId]);

    const combineDateTime = (date, time) => {
        if (date && time) {
            const combinedDateTime = DateTime.fromFormat(
                `${date} ${time}`,
                "yyyy-MM-dd HH:mm:ss"
            ).toISO();
            return combinedDateTime;
        }

        const combinedDateTime = DateTime.fromFormat(
            `${date}`,
            "yyyy-MM-dd"
        ).toISO();
        return combinedDateTime;
    };

    const linkIsAvailable = useMemo(() => {
        return (combinedDateTime.startDateTime || combinedDateTime.endDateTime) && !loadingScheduling && !isChecking;
    }, [combinedDateTime, loadingScheduling, isChecking]);

    const handleGenerateExport = async () => {
        if (!linkIsAvailable) return;
        setLoadingScheduling(true);

        try {
            const response = await axios.post(route('export.sales.excel', { date: combinedDateTime.startDateTime || DateTime.now().toISO(), endDate: combinedDateTime.endDateTime || DateTime.now().toISO() }));
            if (response.data.status === 'success') {
                const messages = {
                    'exportation.scheduled': 'El reporte se está generando.',
                }

                const message = messages[response.data.code];
                toast.success(message);

                setJobId(response.data.job_id);
            }
        } catch (error) {
            console.log({error});
            toast.error('Ocurrió un error al generar el reporte.');
        }

        setLoadingScheduling(false);
    }

    return (
        <Authenticated
            store={props.store}
            auth={props.auth}
            header={
                <h2 className="font-semibold text-xl text-gray-800 leading-tight">
                    Reporte de ventas - TODAS LAS SUCURSALES
                </h2>
            }
        >
            <Head title="Reporte de ventas" />
            <ToastContainer />

            <div className="max-w-7xl flex items-start justify-center flex-col sm:flex-row py-12 px-4 sm:px-0 gap-8 mx-auto w-full">
                <div>
                    <form className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 max-w-md">
                        <div>
                            <Label forInput="start_date" value="Fecha desde:" />
                            <Input
                                className="w-full"
                                value={data.start_date}
                                name="start_date"
                                type="date"
                                handleChange={handleChange}
                            />
                        </div>
                        <div>
                            <Label forInput="start_time" value="Hora desde:" />

                            <Datetime
                                dateFormat={false}
                                timeFormat="HH:mm:ss"
                                inputProps={{ placeholder: 'Seleccione una hora', className: 'w-full border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm' }}
                                onChange={(value) => {
                                    handleChange({ target: { name: 'start_time', value: typeof value === 'string' ? value : value.format('HH:mm:ss') } })
                                }}
                                value={data?.start_time}
                            />
                        </div>

                        <div>
                            <Label forInput="end_date" value="Fecha hasta:" />
                            <Input
                                className="w-full"
                                value={data.end_date}
                                name="end_date"
                                type="date"
                                handleChange={handleChange}
                            />
                        </div>
                        <div>
                            <Label forInput="end_time" value="Hora hasta:" />

                            <Datetime
                                dateFormat={false}
                                timeFormat="HH:mm:ss"
                                inputProps={{ placeholder: 'Seleccione una hora', className: 'w-full border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm' }}
                                onChange={(value) => {
                                    handleChange({ target: { name: 'end_time', value: typeof value === 'string' ? value : value.format('HH:mm:ss') } })
                                }}
                                value={data?.end_time}
                            />
                        </div>
                    </form>
                    <div>
                        <Button style={{ maxWidth: 200 }} className="w-full flex justify-center items-center" onClick={handleGenerateExport} processing={!linkIsAvailable} type="button">
                            {loadingScheduling ? <Spinner width="1rem" height="1rem" weight={2} color="transparent" backgroundColor="#ffffff" /> : 'Generar reporte'}
                        </Button>
                    </div>

                    {isChecking && (
                        <div className="mt-4">
                            <span>Estamos procesando su reporte...</span>
                        </div>
                    )}
                </div>
                <div className="w-full sm:w-auto">
                    {props.exportRequests.length > 0 && (
                        <Table title="Ultimos reportes">
                            <Table.Head>
                                <Table.Row>
                                    <Table.Header>Ordenes desde el</Table.Header>
                                    <Table.Header>Ordenes hasta el</Table.Header>
                                    <Table.Header>Fecha de exportación</Table.Header>
                                    <Table.Header />
                                </Table.Row>
                            </Table.Head>
                            <Table.Body>
                                {props.exportRequests.map(({ id, start_date, end_date, created_at }, index) => (
                                    <Table.Row key={index}>
                                        <Table.Data>
                                            {formatDate(start_date, "dd 'de' ^MMMM 'del' yyyy")} <br />
                                            {formatDate(start_date, 'HH:mm:ss')}
                                        </Table.Data>
                                        <Table.Data>
                                            {formatDate(end_date, "dd 'de' ^MMMM 'del' yyyy")} <br />
                                            {formatDate(end_date, 'HH:mm:ss')}
                                        </Table.Data>
                                        <Table.Data>
                                            {formatDate(created_at, "dd 'de' ^MMMM 'del' yyyy")} <br />
                                            {formatDate(created_at, 'HH:mm:ss')}
                                        </Table.Data>
                                        <Table.Data>
                                            <div className="flex items-center justify-end gap-4">
                                                <ExportButton id={id} />
                                                <RemoveExportButton id={id} />
                                            </div>
                                        </Table.Data>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )}

                </div>
            </div>
        </Authenticated>
    );
};

export default Main;
